/**
 * Leichtgewichtige SVG-Diagramme – themefähig über CSS-Variablen,
 * ohne externe Chart-Bibliothek.
 */

import { formatPercent } from '../lib/format';

/** Ein Datensatz eines Diagramms; `id` stammt aus der Datenbank. */
export type ChartEntry = {
  id?: string | number | null;
  label: string;
  value: number;
  color?: string | null;
};

/**
 * Eindeutiger React-Key. Anzeigenamen taugen nicht dazu: Kritikalitäten,
 * Kategorien oder Personen können denselben Namen tragen. Fehlt die ID,
 * dient die Position als Notnagel.
 */
function entryKey(entry: { id?: string | number | null }, index: number) {
  return entry.id != null ? `id-${entry.id}` : `pos-${index}`;
}

export function BarChart({
  data,
  formatValue = (value: number) => String(value),
}: {
  data: ChartEntry[];
  formatValue?: (value: number) => string;
}) {
  const max = Math.max(1, ...data.map((entry) => entry.value));
  return (
    <div className="bar-chart">
      {data.map((entry, index) => (
        <div className="bar-row" key={entryKey(entry, index)}>
          <span className="truncate" title={entry.label}>
            {entry.label}
          </span>
          <span className="bar-track">
            <span
              className="bar-fill"
              style={{
                width: `${(entry.value / max) * 100}%`,
                background: entry.color ?? 'var(--primary)',
              }}
            />
          </span>
          <span className="value">{formatValue(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Fortschritts-/Quotenbalken, z. B. für die SLA-Erfüllung. */
export function Gauge({ value, label }: { value: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, value));
  const color = clamped >= 95 ? 'var(--success)' : clamped >= 85 ? 'var(--warning)' : 'var(--danger)';
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row between">
        <span className="small muted">{label}</span>
        <strong style={{ color }}>{formatPercent(clamped)}</strong>
      </div>
      <div className="bar-track" style={{ height: 10 }}>
        <div className="bar-fill" style={{ width: `${clamped}%`, background: color }} />
      </div>
    </div>
  );
}
